import React, { useState } from 'react';
import { assets } from '../../assets/assets';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../../components/Sidebar/Sidebar';
import { Howl } from 'howler';
import { useFood } from '../../context/FoodContext';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api.config';

const errorSound = new Howl({ src: ['/sounds/error.mp3'] });
const AddItem = new Howl({ src: ['/sounds/submit.mp3'] });

const Add = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [image, setImage] = useState(false);
  const [existingImage, setExistingImage] = useState('');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [recipeItems, setRecipeItems] = useState([]);
  const [currentIngredient, setCurrentIngredient] = useState({ inventoryId: '', quantity: '', unit: '' });
  const [data, setData] = useState({
    name: '',
    itemCode: '',
    description: '',
    price: '',
    mealType: 'Breakfast',
    category: 'Starter',
    preparationSteps: '',
    nutritionalInfo: {
      calories: 0,
      protein: 0,
      carbohydrates: 0,
      fat: 0,
      fiber: 0,
      sugar: 0,
    },
    reviews: [],
    similarDishes: [],
  });

  const { fetchFoodList } = useFood();

  React.useEffect(() => {
    fetchInventory();
    if (id) {
      fetchFoodDetails(id);
    }
  }, [id]);

  const fetchFoodDetails = async (foodId) => {
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;
      if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;

      const response = await axios.get(buildApiUrl(`/food/${foodId}?restaurantId=${activeRestaurantId}`));
      if (response.data.success) {
        const food = response.data.data;
        setData({
          name: food.name || '',
          itemCode: food.itemCode || '',
          description: food.description || '',
          price: food.price || '',
          mealType: food.mealType || 'Breakfast',
          category: food.category || 'Starter',
          preparationSteps: food.preparationSteps ? food.preparationSteps.join(', ') : '',
          nutritionalInfo: food.nutritionalInfo || { calories: 0, protein: 0, carbohydrates: 0, fat: 0, fiber: 0, sugar: 0 },
          reviews: food.reviews || [],
          similarDishes: food.similarDishes || []
        });
        setRecipeItems(food.recipe || []);
        setExistingImage(food.image || '');
      }
    } catch (error) {
      console.error("Error fetching food details:", error);
      toast.error("Failed to load food details for editing");
    }
  };

  const fetchInventory = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = user?.role === 'owner' ? selectedRestId : user?.restaurantId;
      
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
      }

      if (activeRestaurantId) {
        const response = await axios.get(buildApiUrl(`/inventory/${activeRestaurantId}`));
        if (response.data.success) {
          setInventoryItems(response.data.items || []);
        }
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  const addIngredientToRecipe = () => {
    if (!currentIngredient.inventoryId || !currentIngredient.quantity) {
      toast.error("Please select an ingredient and set quantity");
      return;
    }

    const selectedInv = inventoryItems.find(i => i._id === currentIngredient.inventoryId);
    if (selectedInv) {
      setRecipeItems(prev => [...prev, {
        inventoryId: selectedInv._id,
        name: selectedInv.name,
        quantity: Number(currentIngredient.quantity),
        unit: currentIngredient.unit || selectedInv.unit
      }]);
      setCurrentIngredient({ inventoryId: '', quantity: '', unit: '' });
    }
  };

  const removeIngredientFromRecipe = (index) => {
    setRecipeItems(prev => prev.filter((_, i) => i !== index));
  };

  const onChangeHandler = (event) => {
    const { name, value } = event.target;
    setData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onArrayChangeHandler = (event, field) => {
    const { value } = event.target;
    setData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const onNutritionChangeHandler = (event) => {
    const { name, value } = event.target;
    setData((prevData) => ({
      ...prevData,
      nutritionalInfo: {
        ...prevData.nutritionalInfo,
        [name]: Number(value),
      },
    }));
  };

  const onSubmitHandler = async (event) => {
    event.preventDefault();
    
    // Multi-tenant Scoping: Determine the active restaurant
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const selectedRestId = localStorage.getItem("currentRestaurantId");
    let activeRestaurantId = user.role === 'owner' ? selectedRestId : user.restaurantId;

    // Normalization: Ensure we send a string ID even if the object is populated
    if (activeRestaurantId && typeof activeRestaurantId === 'object') {
        activeRestaurantId = activeRestaurantId._id;
    }

    if (!activeRestaurantId) {
      toast.error("Error: No active restaurant context found.");
      return;
    }

    const preparationStepsArray = data.preparationSteps.split(',').map((item) => item.trim());

    // Automatically derive display ingredients from the structured recipe
    const ingredientsArray = recipeItems.map(item => item.name);

    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('itemCode', data.itemCode);
    formData.append('description', data.description);
    formData.append('price', Number(data.price));
    formData.append('mealType', data.mealType);
    formData.append('category', data.category);
    if (image) {
      formData.append('image', image);
    }
    formData.append('ingredients', JSON.stringify(ingredientsArray));
    formData.append('preparationSteps', JSON.stringify(preparationStepsArray));
    formData.append('nutritionalInfo', JSON.stringify(data.nutritionalInfo));
    formData.append('reviews', JSON.stringify(data.reviews));
    formData.append('similarDishes', JSON.stringify(data.similarDishes));
    formData.append('restaurantId', activeRestaurantId);
    formData.append('recipe', JSON.stringify(recipeItems));

    try {
      let response;
      if (id) {
        response = await axios.put(buildApiUrl(`/food/admin/update/${id}`), formData);
      } else {
        response = await axios.post(buildApiUrl(`/food/admin/add`), formData);
      }
      
      if (response.data.success) {
        if (!id) {
          setData({
            name: '',
            itemCode: '',
            description: '',
            price: '',
            mealType: 'Breakfast',
            category: 'Starter',
            preparationSteps: '',
            nutritionalInfo: {
              calories: 0, protein: 0, carbohydrates: 0, fat: 0, fiber: 0, sugar: 0,
            },
            reviews: [],
            similarDishes: [],
          });
          setRecipeItems([]);
          setImage(false);
        }
        AddItem.play();
        toast.success(response.data.message);
        fetchFoodList();
        
        if (id) {
          navigate('/admin/list');
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'An error occurred while adding the food item.';
      toast.error(errorMsg);
      console.error("Add Food Error:", error);
      errorSound.play();
    }
  };

  return (
    <div className="admin-layout">
      <Sidebar />
      <main>
        <div className="tf-card">
          <form className="tf-grid" onSubmit={onSubmitHandler}>
            <div className="tf-flex-center" style={{ flexDirection: 'column', gap: '10px' }}>
              <p className="tf-text-accent">Upload Image</p>
              <label htmlFor="image">
                <img 
                  src={image ? URL.createObjectURL(image) : existingImage ? `${buildApiUrl(`/uploads/${existingImage}`)}` : assets.upload_area} 
                  alt="" 
                  style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--tf-primary)' }}
                />
              </label>
              <input onChange={(e) => setImage(e.target.files[0])} type="file" id="image" hidden required={!id && !existingImage} />
            </div>

            <div className="tf-form-group" style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 className="tf-text-primary" style={{ margin: 0 }}>{id ? 'Edit Food Item' : 'Create New Food Item'}</h3>
              </div>
            </div>
            
            <div className="tf-flex" style={{ gap: '20px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div className="tf-grid" style={{ flex: 2, minWidth: '250px' }}>
                <p className="tf-text-accent">Product Name</p>
                <input className="tf-input" onChange={onChangeHandler} value={data.name} type="text" name="name" placeholder="Type here" required />
              </div>
              <div className="tf-grid" style={{ flex: 1, minWidth: '150px' }}>
                <p className="tf-text-accent">Item Code (Optional)</p>
                <input className="tf-input" onChange={onChangeHandler} value={data.itemCode} type="text" name="itemCode" placeholder="e.g. 101" />
              </div>
            </div>

            <div className="tf-grid">
              <p className="tf-text-accent">Product Description</p>
              <textarea
                className="tf-input"
                onChange={onChangeHandler}
                value={data.description}
                name="description"
                rows="6"
                placeholder="Write content here"
                required
              ></textarea>
            </div>


            <div className="tf-grid">
              <p className="tf-text-accent">Preparation Steps (comma-separated)</p>
              <input
                className="tf-input"
                onChange={(e) => onArrayChangeHandler(e, 'preparationSteps')}
                value={data.preparationSteps}
                type="text"
                name="preparationSteps"
                placeholder="e.g., Slice tomatoes, Layer with cheese, Bake for 20 minutes"
                required
              />
            </div>

            {/* Recipe Management Section */}
            <div className="tf-grid" style={{ background: 'rgba(255,255,255,0.02)', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="tf-text-accent" style={{ fontSize: '1.2rem', marginBottom: '15px' }}>
                <i className="fa-solid fa-flask" style={{ marginRight: '10px' }}></i> AI Smart Prep Recipe
              </p>
              <p style={{ color: 'var(--tf-text-muted)', fontSize: '0.85rem', marginBottom: '20px' }}>
                Link this dish to your inventory ingredients. The AI uses this to forecast your daily prep needs.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.8fr auto', gap: '10px', alignItems: 'flex-end', marginBottom: '20px' }}>
                <div className="tf-grid">
                  <label style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>Select Ingredient</label>
                  <select 
                    className="tf-input" 
                    value={currentIngredient.inventoryId}
                    onChange={(e) => {
                      const inv = inventoryItems.find(i => i._id === e.target.value);
                      setCurrentIngredient({ 
                        inventoryId: e.target.value, 
                        quantity: currentIngredient.quantity,
                        unit: inv ? inv.unit : ''
                      });
                    }}
                  >
                    <option value="">-- Select from Inventory --</option>
                    {(inventoryItems || []).map(item => (
                      <option key={item._id} value={item._id}>{item.name}</option>
                    ))}
                  </select>
                </div>
                <div className="tf-grid">
                  <label style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>Quantity</label>
                  <input 
                    className="tf-input" 
                    type="number" 
                    placeholder="Qty"
                    value={currentIngredient.quantity}
                    onChange={(e) => setCurrentIngredient({ ...currentIngredient, quantity: e.target.value })}
                  />
                </div>
                <div className="tf-grid">
                  <label style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>Unit</label>
                  <input 
                    className="tf-input" 
                    type="text" 
                    placeholder="gms/pcs"
                    value={currentIngredient.unit}
                    onChange={(e) => setCurrentIngredient({ ...currentIngredient, unit: e.target.value })}
                  />
                </div>
                <button 
                  type="button" 
                  className="tf-btn tf-btn-primary" 
                  style={{ height: '45px', padding: '0 15px' }}
                  onClick={addIngredientToRecipe}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              </div>

              {/* Recipe Items List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(recipeItems || []).map((item, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--tf-primary)' }}>{item.name}</span>
                      <span style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)' }}>{item.quantity} {item.unit}</span>
                    </div>
                    <button 
                      type="button"
                      onClick={() => removeIngredientFromRecipe(index)}
                      style={{ background: 'transparent', border: 'none', color: '#ff4135', cursor: 'pointer', padding: '5px' }}
                    >
                      <i className="fa-solid fa-trash-can"></i>
                    </button>
                  </div>
                ))}
                {recipeItems.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.2)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '12px' }}>
                    No ingredients added to recipe yet.
                  </div>
                )}
              </div>
            </div>

            <div className="tf-grid">
              <p className="tf-text-accent">Nutritional Information</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                <div className="tf-grid" style={{ gap: '5px' }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Calories (kcal)</label>
                  <input
                    className="tf-input"
                    onChange={onNutritionChangeHandler}
                    value={data.nutritionalInfo.calories}
                    type="number"
                    name="calories"
                    placeholder="Calories"
                    required
                  />
                </div>
                <div className="tf-grid" style={{ gap: '5px' }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Protein (g)</label>
                  <input
                    className="tf-input"
                    onChange={onNutritionChangeHandler}
                    value={data.nutritionalInfo.protein}
                    type="number"
                    name="protein"
                    placeholder="Protein"
                    required
                  />
                </div>
                <div className="tf-grid" style={{ gap: '5px' }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Carbohydrates (g)</label>
                  <input
                    className="tf-input"
                    onChange={onNutritionChangeHandler}
                    value={data.nutritionalInfo.carbohydrates}
                    type="number"
                    name="carbohydrates"
                    placeholder="Carbohydrates"
                    required
                  />
                </div>
                <div className="tf-grid" style={{ gap: '5px' }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Fat (g)</label>
                  <input
                    className="tf-input"
                    onChange={onNutritionChangeHandler}
                    value={data.nutritionalInfo.fat}
                    type="number"
                    name="fat"
                    placeholder="Fat"
                    required
                  />
                </div>
                <div className="tf-grid" style={{ gap: '5px' }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Fiber (g)</label>
                  <input
                    className="tf-input"
                    onChange={onNutritionChangeHandler}
                    value={data.nutritionalInfo.fiber}
                    type="number"
                    name="fiber"
                    placeholder="Fiber"
                    required
                  />
                </div>
                <div className="tf-grid" style={{ gap: '5px' }}>
                  <label style={{ color: 'var(--tf-text-muted)', fontSize: '0.9rem' }}>Sugar (g)</label>
                  <input
                    className="tf-input"
                    onChange={onNutritionChangeHandler}
                    value={data.nutritionalInfo.sugar}
                    type="number"
                    name="sugar"
                    placeholder="Sugar"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="tf-flex" style={{ gap: '20px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div className="tf-grid" style={{ flex: 1, minWidth: '150px' }}>
                <p className="tf-text-accent">Meal Type</p>
                <select className="tf-input" onChange={onChangeHandler} name="mealType" value={data.mealType}>
                  <option value="Breakfast">Breakfast</option>
                  <option value="Lunch">Lunch</option>
                  <option value="Dinner">Dinner</option>
                </select>
              </div>
              
              <div className="tf-grid" style={{ flex: 1, minWidth: '150px' }}>
                <p className="tf-text-accent">Category</p>
                <select className="tf-input" onChange={onChangeHandler} name="category" value={data.category}>
                  <option value="Starter">Starter</option>
                  <option value="Main Course">Main Course</option>
                  <option value="Dessert">Dessert</option>
                </select>
              </div>
              
              <div className="tf-grid" style={{ flex: 1, minWidth: '150px' }}>
                <p className="tf-text-accent">Product Price</p>
                <input
                  className="tf-input"
                  onChange={onChangeHandler}
                  value={data.price}
                  type="number"
                  name="price"
                  placeholder="20"
                  required
                />
              </div>
            </div>

            <button type="submit" className="tf-btn tf-btn-primary" style={{ alignSelf: 'flex-end', width: '150px' }}>
              {id ? 'UPDATE' : 'ADD'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Add;